import { useSimStore } from '../../store/useSimStore';
import { useBroadcastSync } from '../../sync/useBroadcastSync';
import { useWebSocketSync } from '../../sync/useWebSocketSync';
import MonitorDisplay from './MonitorDisplay';
import VentilatorScreen from '../ventilator/VentilatorScreen';
import FetalMonitor from '../fetal/FetalMonitor';
import EegMonitor from '../eeg/EegMonitor';
import styles from './MonitorRoute.module.css';

/** Ventilator shown on its own screen — mounts the sync receivers itself. */
function VentStandalone() {
  useBroadcastSync(false);
  useWebSocketSync(false);
  return <VentilatorScreen />;
}

/** Fetal CTG shown on its own screen — mounts the sync receivers itself. */
function FetalStandalone() {
  useBroadcastSync(false);
  useWebSocketSync(false);
  return <FetalMonitor />;
}

/** Depth-of-anaesthesia monitor on its own screen. */
function EegStandalone() {
  useBroadcastSync(false);
  useWebSocketSync(false);
  return <EegMonitor />;
}

/**
 * /monitor route wrapper.
 *
 * Renders the patient monitor alone, or — when a second screen is enabled —
 * side by side with the ventilator and/or the intrapartum fetal (CTG) monitor.
 * The instructor's small preview iframe passes ?view=monitor to force
 * monitor-only so the preview stays legible.
 *
 * URL overrides: ?view=monitor (monitor only), ?view=split (force vent split),
 * ?view=vent (ventilator standalone), ?view=fetal (CTG standalone).
 */
export default function MonitorRoute() {
  const splitScreen = useSimStore((s) => s.splitScreen);
  const fetalMonitor = useSimStore((s) => s.fetalMonitor);
  const eegOn = useSimStore((s) => s.eegMonitor.enabled);

  const view = new URLSearchParams(window.location.search).get('view');
  if (view === 'vent') return <VentStandalone />;
  if (view === 'fetal') return <FetalStandalone />;
  if (view === 'eeg') return <EegStandalone />;

  const monitorOnly = view === 'monitor';
  const showVent = view === 'split' || (splitScreen && !monitorOnly);
  const showFetal = fetalMonitor && !monitorOnly;
  const showEeg = eegOn && !monitorOnly;

  // MonitorDisplay must keep the SAME position in the element tree whether or
  // not a second pane is shown. Returning it bare in one branch and wrapped in
  // divs in the other made React unmount/remount it whenever split screen
  // toggled — losing all its local state, most audibly the user-gesture-unlocked
  // AudioContext (alarms and the SpO₂ beep fell silent until the monitor was
  // clicked again). A lone flex pane simply fills the screen, so the wrapper is
  // unconditional; keys keep each pane's identity stable as siblings appear.
  //
  // The fetal CTG is a horizontal strip (like a real paper trace) that runs
  // full-width BELOW the monitor/ventilator row, so the top row wrapper is
  // unconditional too.
  return (
    <div className={styles.split}>
      <div className={styles.row}>
        <div key="monitor" className={styles.pane}><MonitorDisplay /></div>
        {showVent && <div key="vent" className={styles.pane}><VentilatorScreen /></div>}
        {showEeg && <div key="eeg" className={`${styles.pane} ${styles.paneNarrow}`}><EegMonitor /></div>}
      </div>
      {showFetal && <div key="fetal" className={styles.strip}><FetalMonitor layout="strip" /></div>}
    </div>
  );
}
